/**
 * Date filters that read the way people here write dates.
 *
 * A native `<input type="date">` renders its own empty-state hint, and Chrome
 * writes that hint in lower case — `dd-mm-yyyy`. Nothing can change it: it is
 * browser chrome rather than a placeholder, so there is no attribute, no
 * property and no stylesheet that reaches it. The only way to show
 * `DD-MM-YYYY` is to stop using a native date input and take the parsing on.
 *
 * That is a fair trade for a *filter* and a bad one for a form. A filter is
 * typed quickly by somebody who already knows the date they want, and losing
 * the calendar popover costs them nothing. A joining date or a date of birth
 * is a field somebody picks, often on a phone, and replacing that control with
 * a text box would trade a real affordance for a casing preference. So these
 * two helpers exist for filters, and the forms keep their native inputs.
 *
 * Both directions are deliberately forgiving in one direction only. Anything
 * that is not eight digits comes back as an empty string rather than a guess,
 * because a half-typed date must narrow nothing — the alternative is a list
 * that empties itself while somebody is still typing the year.
 */

/** ISO (what the API speaks) to DD-MM-YYYY (what the screen shows). */
export function formatFilterDate(isoDate: string): string {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return year && month && day ? `${day}-${month}-${year}` : '';
}

/*
 * `parseFilterDate` and `sanitiseFilterDateInput` lived here until the four
 * date filters became native `type="date"` inputs and stopped needing to parse
 * anything -- that control's value is already ISO. They are in the history if
 * the picker's locale-driven display turns out wrong for these users and the
 * typed DD-MM-YYYY box has to come back.
 */
